import React, { useState, useRef, useImperativeHandle } from 'react';
import styled from 'styled-components';
import { EnvelopeIcon } from '../shared/Icons';

const StyledWrapper = styled.div`
  width: 100%;
  margin: 8px 0;

  .email-input-wrap {
    position: relative;
    display: block;
    width: 100%;
  }

  /* Centre off the wrapper rather than flex-aligning, so the icon stays
     vertically centred at any themed input height. */
  .email-input-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 0;
    color: #9ca3af;
    pointer-events: none;
    z-index: 1;
  }

  .email-input {
    width: 100%;
    border: 1px solid #c4c4c4;
    border-radius: 4px;
    padding: 8.5px 14px;
    font-size: 1rem;
    font-family: inherit;
    outline: none;
    box-sizing: border-box;
    transition: border-color 0.15s ease;
  }

  .email-input.with-icon {
    padding-left: 38px;
  }

  /* Border width must not change on focus: a 1px -> 2px swap reflows the text
     box and makes the icon/text drift out of alignment. */
  .email-input:focus {
    border-color: #0069ff;
  }

  &.has-error .email-input {
    border-color: #d32f2f;
  }

  .email-input-error-text {
    color: #d32f2f;
    font-size: 0.75rem;
    margin-top: 3px;
  }

  .email-input-suggestion {
    color: #666;
    font-size: 0.75rem;
    margin-top: 3px;
  }

  .email-input-suggestion button {
    background: none;
    border: none;
    padding: 0;
    color: #0069ff;
    cursor: pointer;
    font-size: 0.75rem;
    text-decoration: underline;
  }
`;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const COMMON_DOMAINS = [
  'gmail.com',
  'yahoo.com',
  'hotmail.com',
  'outlook.com',
  'icloud.com',
];

// Levenshtein distance, capped for short domain strings so this stays cheap on every keystroke.
const levenshtein = (a: string, b: string): number => {
  const dp: number[][] = Array.from({ length: a.length + 1 }, () =>
    new Array(b.length + 1).fill(0),
  );
  for (let i = 0; i <= a.length; i++) dp[i][0] = i;
  for (let j = 0; j <= b.length; j++) dp[0][j] = j;
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      dp[i][j] =
        a[i - 1] === b[j - 1]
          ? dp[i - 1][j - 1]
          : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
    }
  }
  return dp[a.length][b.length];
};

const suggestDomain = (email: string): string | null => {
  const at = email.lastIndexOf('@');
  if (at === -1) return null;
  const domain = email.slice(at + 1).toLowerCase();
  if (!domain || COMMON_DOMAINS.includes(domain)) return null;

  let best: string | null = null;
  let bestDistance = Infinity;
  for (const candidate of COMMON_DOMAINS) {
    const distance = levenshtein(domain, candidate);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = candidate;
    }
  }
  return best && bestDistance > 0 && bestDistance <= 2
    ? email.slice(0, at + 1) + best
    : null;
};

interface EmailInputData {
  field_name?: string;
  required?: boolean;
  value?: string;
  showIcon?: boolean;
  placeholder?: string;
}

interface EmailInputProps {
  data: EmailInputData;
  onChange?: (value: Record<string, string>) => void;
  read_only?: boolean;
  mutable?: boolean;
}

const EmailInput = React.forwardRef<HTMLInputElement, EmailInputProps>(
  (props, ref) => {
    const {
      field_name,
      required,
      value: initialValue,
      showIcon = true,
      placeholder,
    } = props.data || {};

    const [value, setValue] = useState<string>(initialValue || '');
    const [touched, setTouched] = useState(false);
    const hiddenInputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => hiddenInputRef.current as HTMLInputElement);

    const isValid = !value || EMAIL_REGEX.test(value);
    const showError =
      props.mutable !== false &&
      touched &&
      (required ? !value || !isValid : Boolean(value) && !isValid);
    const suggestion = isValid && value ? suggestDomain(value) : null;

    const emitChange = (next: string) => {
      setValue(next);
      if (props.onChange && field_name) {
        props.onChange({ [field_name]: next });
      }
    };

    const applySuggestion = () => {
      if (suggestion) emitChange(suggestion);
    };

    return (
      <StyledWrapper className={showError ? 'has-error' : ''}>
        <div className="email-input-wrap ahd-icon-input-wrap">
          {showIcon && (
            <span className="email-input-icon ahd-icon-input-icon">
              <EnvelopeIcon size={18} />
            </span>
          )}
          <input
            className={`email-input${showIcon ? ' with-icon' : ''}`}
            type="email"
            name={field_name}
            placeholder={placeholder || ''}
            value={value}
            disabled={props.read_only}
            onChange={(e) => emitChange(e.target.value)}
            onBlur={() => setTouched(true)}
          />
        </div>
        {showError && (
          <div className="email-input-error-text">
            {required && !value ? 'This field is required.' : 'Enter a valid email address.'}
          </div>
        )}
        {!showError && suggestion && (
          <div className="email-input-suggestion">
            Did you mean{' '}
            <button type="button" onClick={applySuggestion}>
              {suggestion}
            </button>
            ?
          </div>
        )}
        <input ref={hiddenInputRef} type="hidden" name={field_name} value={value} />
      </StyledWrapper>
    );
  },
);

EmailInput.displayName = 'EmailInput';

export default EmailInput;
